import { ReactNode, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { AlertTriangle } from 'lucide-react'
import { cn } from 'utils/cn'
import { isTopmostOverlay, lockBodyScroll } from './overlayStack'

export interface AlertDialogProps {
    open: boolean
    onClose: () => void
    title: string
    description?: string
    descriptionTone?: 'default' | 'warning'
    icon?: ReactNode
    headerAlign?: 'left' | 'center'
    size?: 'xs' | 'sm' | 'md' | 'lg'
    confirmLabel?: string
    cancelLabel?: string
    /** When the cancel button is a second explicit choice; backdrop and Escape still use onClose. */
    onCancel?: () => void
    onConfirm?: () => void
    variant?: 'default' | 'accent' | 'success' | 'warning' | 'danger'
    footerLayout?: 'horizontal' | 'vertical'
}

const sizeWidths: Record<string, number> = {
    xs: 320,
    sm: 480,
    md: 640,
    lg: 800,
}

// Divergence from @numeo-ai/components: the library dialog appears instantly. Modal already animates
// in here, and a confirm that pops with no transition next to an animated modal reads as a glitch.
// Tailwind's translate utilities handle centering; animating translate here would apply it twice.
const keyframesStyle = `
@keyframes numeo-alert-backdrop-in { from { opacity: 0 } to { opacity: 1 } }
@keyframes numeo-alert-in { from { opacity: 0; transform: scale(0.97) } to { opacity: 1; transform: scale(1) } }
`

const iconColorClasses: Record<string, string> = {
    default: 'text-default',
    accent: 'text-accent',
    success: 'text-success',
    warning: 'text-warning',
    danger: 'text-danger',
}

const confirmClasses: Record<string, string> = {
    default: 'bg-default text-default-foreground hover:bg-default-hover',
    accent: 'bg-accent text-accent-foreground hover:bg-accent-hover',
    success: 'bg-success text-success-foreground hover:bg-success-hover',
    warning: 'bg-warning text-warning-foreground hover:bg-warning-hover',
    danger: 'bg-danger text-danger-foreground hover:bg-danger-hover',
}

// The DS focus ring, which the library's own button spec mandates and its AlertDialog port omits —
// without it the two buttons are unreachable-looking for anyone driving this by keyboard.
const focusRing = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface'

export const AlertDialog = ({
    open,
    onClose,
    title,
    description,
    descriptionTone = 'default',
    icon,
    headerAlign = 'left',
    size = 'sm',
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    onCancel,
    onConfirm,
    variant = 'danger',
    footerLayout = 'horizontal',
}: AlertDialogProps) => {
    const dialogRef = useRef<HTMLDivElement>(null)
    const confirmRef = useRef<HTMLButtonElement>(null)

    useEffect(() => {
        if (!open) return
        const handleKey = (e: KeyboardEvent) => {
            if (!isTopmostOverlay(dialogRef.current)) return
            if (e.key === 'Escape') {
                onClose()
                return
            }
            // Keep Tab inside the dialog. Two buttons and a backdrop: without the wrap, one Tab walks
            // the focus ring out onto the blurred page behind and the user cannot see where it went.
            if (e.key !== 'Tab' || !dialogRef.current) return
            const focusable = dialogRef.current.querySelectorAll<HTMLElement>('button:not([disabled])')
            if (focusable.length === 0) return
            const first = focusable[0]
            const last = focusable[focusable.length - 1]
            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault()
                last.focus()
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault()
                first.focus()
            }
        }
        document.addEventListener('keydown', handleKey)
        return () => document.removeEventListener('keydown', handleKey)
    }, [open, onClose])

    // Scroll lock, matching Modal — the page scrolling behind a blurred backdrop looks broken.
    useEffect(() => {
        if (!open) return
        return lockBodyScroll()
    }, [open])

    // Land the focus on the confirm button and give it back to whatever opened the dialog on close,
    // so Enter answers the question and the caller's row/button is still focused afterwards.
    useEffect(() => {
        if (!open) return
        const previouslyFocused = document.activeElement as HTMLElement | null
        confirmRef.current?.focus()
        return () => previouslyFocused?.focus?.()
    }, [open])

    if (!open) return null

    const isCenter = headerAlign === 'center'
    const isVertical = footerLayout === 'vertical'

    const dialog = (
        <>
            <style>{keyframesStyle}</style>

            {/* Backdrop */}
            <div
                onClick={(event) => {
                    event.stopPropagation()
                    onClose()
                }}
                className="fixed inset-0 z-[9998] animate-[numeo-alert-backdrop-in_0.2s_ease] bg-black/50 backdrop-blur-[12px] motion-reduce:animate-none"
            />

            {/* Dialog */}
            <div
                ref={dialogRef}
                data-ds2-overlay
                role="alertdialog"
                aria-modal="true"
                aria-labelledby="alert-dialog-title"
                aria-describedby={description ? 'alert-dialog-description' : undefined}
                onClick={(event) => event.stopPropagation()}
                className="fixed left-1/2 top-1/2 z-[9999] flex max-w-[calc(100vw-32px)] -translate-x-1/2 -translate-y-1/2 animate-[numeo-alert-in_0.2s_ease] flex-col overflow-hidden rounded-3xl bg-surface shadow-overlay motion-reduce:animate-none"
                style={{ width: sizeWidths[size] }}
            >
                {/* Header */}
                <div className={cn('flex flex-col gap-2 px-6 pt-6', isCenter ? 'items-center text-center' : 'items-start text-left')}>
                    <span className={cn('mb-1', iconColorClasses[variant])}>{icon !== undefined ? icon : <AlertTriangle size={24} strokeWidth={1.5} />}</span>
                    <span id="alert-dialog-title" className="text-base font-semibold leading-[1.5] text-foreground">
                        {title}
                    </span>
                </div>

                {/* Body */}
                {description && (
                    <div
                        id="alert-dialog-description"
                        className={cn(
                            'px-6 pt-2 text-sm leading-[1.43]',
                            descriptionTone === 'warning' ? 'font-semibold text-warning' : 'text-foreground-muted',
                            isCenter ? 'text-center' : 'text-left'
                        )}
                    >
                        {description}
                    </div>
                )}

                {/* Footer — stacks below 400px, where two pills side by side leave neither room to read. */}
                <div
                    className={cn(
                        'flex p-6 max-[400px]:flex-col-reverse max-[400px]:gap-2',
                        isVertical ? 'flex-col-reverse justify-center gap-2' : 'flex-row justify-end gap-3'
                    )}
                >
                    {cancelLabel && (
                        <button
                            type="button"
                            onClick={onCancel ?? onClose}
                            className={cn(
                                'flex h-9 items-center justify-center rounded-3xl bg-default px-4 text-sm font-medium leading-5 text-default-foreground transition-colors duration-150 hover:bg-default-hover max-[400px]:w-full',
                                focusRing
                            )}
                        >
                            {cancelLabel}
                        </button>
                    )}
                    <button
                        ref={confirmRef}
                        type="button"
                        onClick={() => {
                            onConfirm?.()
                            onClose()
                        }}
                        className={cn(
                            'flex h-9 items-center justify-center rounded-3xl px-4 text-sm font-medium leading-5 transition-colors duration-150 max-[400px]:w-full',
                            confirmClasses[variant],
                            focusRing
                        )}
                    >
                        {confirmLabel}
                    </button>
                </div>
            </div>
        </>
    )

    // Portal to body, same as Modal: this dialog positions itself with `position: fixed`, and a CSS
    // transform on any ancestor re-anchors that to the ancestor instead of the viewport. Opened from
    // inside a sliding Drawer — which animates with translateX — it would otherwise be centred within
    // the drawer panel rather than on the screen.
    return createPortal(dialog, document.body)
}
